/**
 * Options for sanitizing a path.
 */
interface SanitizeOptions {
	/**
	 * Whether to lowercase the path.
	 */
	lowercase?: boolean;

	/**
	 * String to replace spaces with. If `true`, spaces are replaced with underscores.
	 */
	replaceSpaces?: boolean | string;

	/**
	 * Whether to remove the trailing slash.
	 */
	removeTrailingSlash?: boolean;
}

/**
 * Sanitizes a path by removing all characters except for letters, numbers, hyphens, underscores, dots, and slashes.
 * @param {string} path Path to sanitize
 * @param {SanitizeOptions} options Options for sanitizing the path
 * @returns {string} Sanitized path
 */
export function toSanitizedPath(
	path: string,
	options?: SanitizeOptions,
): string {
	if (!path) {
		return path;
	}

	// Set default options
	const {
		lowercase = false,
		replaceSpaces = '-',
		removeTrailingSlash = true,
	} = options || {};

	// Trim the path
	path = path.trim();

	// Replace spaces with the specified string
	if (replaceSpaces) {
		path = path.replace(/\s+/g, replaceSpaces === true ? '_' : replaceSpaces);
	}

	// Remove all characters except for letters, numbers, hyphens, underscores, dots, and slashes
	path = path.replace(/[^a-z0-9-_.\/]/gi, '');

	// Optionally lowercase the path
	if (lowercase) {
		path = path.toLowerCase();
	}

	// Remove the trailing slash
	if (removeTrailingSlash) {
		path = path.replace(/\/$/, '');
	}

	return path;
}
